import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Select,
  Divider,
  Tooltip,
  Badge,
  Checkbox,
  Dropdown,
  Statistic,
  DatePicker,
  Alert,
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SyncOutlined,
  SaveOutlined,
  ExportOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CloudDownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { authService } from '../services/authService';
import { companyService, Company, buildProjectIdSelectOptions } from '../services/companyService';
import './common.css';
import { useI18n } from '../hooks/useI18n';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const API_BASE_URL = '/api';

// 辅助函数：创建带认证头的fetch请求
const createAuthenticatedFetch = (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const currentTenant = localStorage.getItem('currentTenant') || 'default';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  headers['X-Tenant-Code'] = currentTenant;

  return fetch(url, {
    ...options,
    headers,
  });
};

interface SqlPair {
  id: number;
  tenant_code: string;
  project_id?: number;
  chatbi_sql_pair_id?: number | null;
  question: string;
  sql: string;
  optimized_query?: string | null;
  business_rules?: string | null;
  expires_at?: number | null;
  is_synchronized: number;
  is_active?: number;
  last_sync_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface SqlPairFormData {
  id?: number;
  question: string;
  sql: string;
  optimized_query?: string;
  business_rules?: string;
  expires_at?: any;
  is_synchronized: number;
  project_id?: number;
}

const SqlPairManager: React.FC = () => {
  const { t } = useI18n();
  const [sqlPairs, setSqlPairs] = useState<SqlPair[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSqlPair, setEditingSqlPair] = useState<SqlPairFormData | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIsSynchronized, setFilterIsSynchronized] = useState<string>('');

  // 页面项目筛选：全部 | projectId
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [companies, setCompanies] = useState<Company[]>([]);

  const projectSelectOptions = useMemo(() => {
    const base = buildProjectIdSelectOptions(companies, t('common.all'));
    const existingValues = new Set(base.map((o) => o.value));
    const extras = Array.from(
      new Set(
        sqlPairs
          .map((sp) => sp.project_id)
          .filter((v) => v !== undefined && v !== null)
          .map((v) => String(v))
      )
    )
      .filter((id) => !existingValues.has(id))
      .map((id) => ({ value: id, label: `ProjectId: ${id}` }));
    return [...base, ...extras];
  }, [companies, sqlPairs, t]);

  const [syncingSqlPairId, setSyncingSqlPairId] = useState<number | null>(null);
  const [syncErrorSqlPairId, setSyncErrorSqlPairId] = useState<number | null>(null);
  const [syncErrorMessage, setSyncErrorMessage] = useState<string>('');
  const [batchSyncing, setBatchSyncing] = useState(false);
  const [form] = Form.useForm();

  // 多选相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedSqlPairs, setSelectedSqlPairs] = useState<SqlPair[]>([]);

  // 备份相关状态
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupFiles, setBackupFiles] = useState<any[]>([]);
  const [backupFilesVisible, setBackupFilesVisible] = useState(false);

  // 导入相关
  const [importVisible, setImportVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importInputKey, setImportInputKey] = useState(0);

  // 是否为超级管理员
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      const obj = JSON.parse(raw);
      return !!obj?.isSuperAdmin;
    } catch {
      return false;
    }
  });

  // 获取当前 tenant 对应的 tenant_id
  const getCurrentTenantId = (): string => {
    const currentTenantCode = localStorage.getItem('currentTenant') || 'default';
    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) return String(currentTenantCode);
      const obj = JSON.parse(rawUser);
      const tenants = obj?.tenants;
      if (Array.isArray(tenants)) {
        const match = tenants.find(
          (t: any) => t?.code === currentTenantCode || t?.tenant_code === currentTenantCode
        );
        const id = match?.id ?? obj?.tenant_id ?? obj?.tenantId;
        if (id !== undefined && id !== null) return String(id);
      }
      const fallbackId = obj?.tenant_id ?? obj?.tenantId;
      return fallbackId !== undefined && fallbackId !== null ? String(fallbackId) : String(currentTenantCode);
    } catch {
      return String(currentTenantCode);
    }
  };

  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ─────────────────────── 加载数据 ───────────────────────
  const loadData = async () => {
    setLoading(true);
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';

      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs${isSuperAdmin ? '?isSuperAdmin=true' : ''}`,
        {
          headers: isSuperAdmin ? { 'x-is-super-admin': 'true' } : {},
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch sql-pairs: ${response.status}`);
      }
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to load sql-pairs');
      }
      setSqlPairs(result.data?.sqlPairs || []);

      // 从公司列表加载 project 下拉选项（不依赖 sql-pairs 是否已有数据）
      try {
        const companyResp = await companyService.getCompanies(currentTenant);
        setCompanies(companyResp.data || []);
      } catch (e) {
        console.warn('Failed to load companies for project labels:', e);
        setCompanies([]);
      }

      message.success(t('sqlPair.loadSuccess'));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to load sql-pairs:', error);
      message.error(t('sqlPair.loadFailedWithReason', { reason: errorMessage }));
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────── 创建 ───────────────────────
  const handleCreate = () => {
    setIsEditMode(false);
    setEditingSqlPair({
      question: '',
      sql: '',
      optimized_query: '',
      business_rules: '',
      expires_at: null,
      is_synchronized: 0,
      project_id: selectedProjectId !== 'all' ? Number(selectedProjectId) : 1,
    });
    form.setFieldsValue({
      id: undefined,
      question: '',
      sql: '',
      optimized_query: '',
      business_rules: '',
      expires_at: null,
      is_synchronized: 0,
    });
    setModalVisible(true);
  };

  // ─────────────────────── 编辑 ───────────────────────
  const handleEdit = (sqlPair: SqlPair) => {
    setIsEditMode(true);
    setEditingSqlPair({
      id: sqlPair.id,
      question: sqlPair.question,
      sql: sqlPair.sql,
      optimized_query: sqlPair.optimized_query || '',
      business_rules: sqlPair.business_rules || '',
      expires_at: sqlPair.expires_at ? dayjs.unix(sqlPair.expires_at) : null,
      is_synchronized: sqlPair.is_synchronized,
      project_id: sqlPair.project_id,
    });
    form.setFieldsValue({
      id: sqlPair.id,
      question: sqlPair.question,
      sql: sqlPair.sql,
      optimized_query: sqlPair.optimized_query || '',
      business_rules: sqlPair.business_rules || '',
      expires_at: sqlPair.expires_at ? dayjs.unix(sqlPair.expires_at) : null,
      is_synchronized: sqlPair.is_synchronized,
    });
    setModalVisible(true);
  };

  // ─────────────────────── 软删除 ───────────────────────
  const handleDelete = async (id: number) => {
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/${id}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(`Failed to delete sql-pair: ${response.status}`);
      }
      message.success(t('sqlPair.deleteSuccess'));
      setSqlPairs(prev =>
        prev.map(sp => (sp.id === id ? { ...sp, is_active: 0, is_synchronized: 0 } : sp))
      );
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to delete sql-pair:', error);
      message.error(t('sqlPair.deleteFailedWithReason', { reason: errorMessage }));
    }
  };

  // ─────────────────────── 同步（单行）───────────────────────
  const handleSync = async (sqlPair: SqlPair) => {
    try {
      setSyncingSqlPairId(sqlPair.id);
      const currentTenant = authService.getCurrentTenant() || 'default';
      const tenantId = getCurrentTenantId();
      const projectId =
        selectedProjectId !== 'all'
          ? String(selectedProjectId)
          : String(sqlPair.project_id ?? 1);

      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/${sqlPair.id}/sync`,
        {
          method: 'POST',
          body: JSON.stringify({}),
          headers: {
            'X-Tenant-Id': tenantId,
            'X-Project-Id': projectId,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        const errMsg = result.message || result.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errMsg);
      }

      if (result.success) {
        message.success(t('sqlPair.syncSuccess'));
        setSyncErrorSqlPairId(null);
        setSyncErrorMessage('');

        if (sqlPair.is_active === 0) {
          setSqlPairs(prev => prev.filter(sp => sp.id !== sqlPair.id));
          message.info(t('sqlPair.deletedSqlPairSyncedAndRemoved'));
        } else {
          setSqlPairs(prev =>
            prev.map(sp => (sp.id === sqlPair.id ? { ...sp, is_synchronized: 1 } : sp))
          );
        }
      } else {
        const errMsg = result.message || result.error || t('sqlPair.syncFailedUnknown');
        message.error(t('sqlPair.syncFailedWithReason', { reason: errMsg }));
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to sync sql-pair:', error);
      setSyncErrorSqlPairId(sqlPair.id);
      setSyncErrorMessage(
        t('sqlPair.syncFailedLine', {
          name: sqlPair.question.substring(0, 50),
          reason: errorMessage,
        })
      );
      message.warning(t('sqlPair.checkServerAndNetwork'));
      setTimeout(() => {
        setSyncErrorSqlPairId(null);
      }, 3000);
    } finally {
      setSyncingSqlPairId(null);
    }
  };

  // ─────────────────────── 保存 ───────────────────────
  const handleSave = async (values: Record<string, unknown>) => {
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';

      const projectIdToSave =
        editingSqlPair?.project_id !== undefined && editingSqlPair?.project_id !== null
          ? editingSqlPair.project_id
          : selectedProjectId !== 'all'
          ? Number(selectedProjectId)
          : 1;

      const sqlPairData = {
        question: values.question,
        sql: values.sql,
        optimized_query: (values.optimized_query as string) || null,
        business_rules: (values.business_rules as string) || null,
        expires_at: values.expires_at ? dayjs(values.expires_at as any).unix() : null,
        project_id: projectIdToSave,
        is_synchronized: values.is_synchronized || 0,
      };

      if (isEditMode && editingSqlPair?.id) {
        const response = await createAuthenticatedFetch(
          `${API_BASE_URL}/${currentTenant}/sql-pairs/${editingSqlPair.id}`,
          {
            method: 'PUT',
            body: JSON.stringify({ update_data: sqlPairData }),
          }
        );
        if (!response.ok) {
          const result = await response.json();
          throw new Error(result.message || `Failed to update: ${response.status}`);
        }
      } else {
        const response = await createAuthenticatedFetch(
          `${API_BASE_URL}/${currentTenant}/sql-pairs`,
          {
            method: 'POST',
            body: JSON.stringify({
              sql_pair_data: {
                ...sqlPairData,
                tenant_code: currentTenant,
              },
            }),
          }
        );
        if (!response.ok) {
          const result = await response.json();
          throw new Error(result.message || `Failed to create: ${response.status}`);
        }
      }

      message.success(isEditMode ? t('sqlPair.updateSuccess') : t('sqlPair.createSuccess'));
      setModalVisible(false);
      loadData();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to save sql-pair:', error);
      message.error(t('sqlPair.saveFailedWithReason', { reason: errorMessage }));
    }
  };

  // ─────────────────────── 备份本地 ───────────────────────
  const handleBackupSqlPairs = async () => {
    setBackupLoading(true);
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/backup`,
        { method: 'POST' }
      );
      const result = await response.json();
      if (result.success) {
        message.success(t('sqlPair.backupSuccess', { filename: result.data?.filename || '' }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('sqlPair.backupFailed'));
      }
    } catch (error) {
      console.error('Failed to backup sql-pairs:', error);
      message.error(t('sqlPair.backupFailed'));
    } finally {
      setBackupLoading(false);
    }
  };

  // ─────────────────────── 备份远程 ───────────────────────
  const handleBackupRemoteSqlPairs = async () => {
    setBackupLoading(true);
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/backup-remote`,
        { method: 'POST' }
      );
      const result = await response.json();
      if (result.success) {
        message.success(t('sqlPair.backupSuccess', { filename: result.data?.filename || '' }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('sqlPair.backupFailed'));
      }
    } catch (error) {
      console.error('Failed to backup remote sql-pairs:', error);
      message.error(t('sqlPair.backupFailed'));
    } finally {
      setBackupLoading(false);
    }
  };

  // ─────────────────────── 加载备份文件列表 ───────────────────────
  const loadBackupFiles = async () => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/backup/files`
      );
      const result = await response.json();
      if (result.success) {
        setBackupFiles(result.data);
      }
    } catch (error) {
      console.error('Failed to load backup files:', error);
    }
  };

  // ─────────────────────── 下载备份文件（JSON）───────────────────────
  const handleDownloadBackupFile = async (filename: string) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/backup/${filename}`
      );
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(t('sqlPair.fileDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error(t('sqlPair.fileDownloadFailed'));
    }
  };

  // ─────────────────────── 下载备份文件（Excel）───────────────────────
  const handleDownloadBackupFileAsExcel = async (filename: string) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/backup/${filename}/excel`
      );
      if (!response.ok) {
        throw new Error('下载失败');
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      let downloadFilename = filename.replace('.json', '.xlsx');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) {
          downloadFilename = decodeURIComponent(matches[1]);
        }
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(t('sqlPair.excelDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download Excel file:', error);
      message.error(t('sqlPair.excelDownloadFailed'));
    }
  };

  // ─────────────────────── 显示备份文件列表 ───────────────────────
  const handleShowBackupFiles = () => {
    setBackupFilesVisible(true);
    loadBackupFiles();
  };

  // ─────────────────────── 导出数据（本地JSON）───────────────────────
  const exportData = () => {
    const data = { data: { sqlPairs } };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sql-pairs-local-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(t('sqlPair.dataExportSuccess'));
  };

  const getImportProjectId = (): string | null => {
    if (selectedProjectId !== 'all') return selectedProjectId;
    const first = projectSelectOptions.find((o) => o.value !== 'all');
    return first?.value ?? null;
  };

  // ─────────────────────── 导入 ───────────────────────
  const handleImportFromJson = async () => {
    if (!importFile) {
      message.info(t('sqlPair.noFileSelected'));
      return;
    }
    const importProjectId = getImportProjectId();
    if (!importProjectId) {
      message.warning(t('sqlPair.importSelectProjectFirst'));
      return;
    }
    setImporting(true);
    try {
      const text = await importFile.text();
      const json = JSON.parse(text);
      const currentTenant = authService.getCurrentTenant() || 'default';
      const resp = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/import`,
        {
          method: 'POST',
          headers: {
            'X-Project-Id': importProjectId,
          },
          body: JSON.stringify({
            ...json,
            project_id: Number(importProjectId),
          }),
        }
      );
      const result = await resp.json();
      if (!resp.ok || !result.success) {
        throw new Error(result.message || String(resp.status));
      }
      const stats = result.data
        ? ` (${t('sqlPair.importStats', {
            created: result.data.created || 0,
            updated: result.data.updated || 0,
            failed: result.data.failed || 0,
          })})`
        : '';
      message.success(t('sqlPair.importCompleted') + stats);
      setImportVisible(false);
      setImportFile(null);
      setImportInputKey(k => k + 1);
      await loadData();
    } catch (e: any) {
      message.error(e?.message || t('sqlPair.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  // ─────────────────────── 导入默认文件 ───────────────────────
  const handleImportDefault = async () => {
    const importProjectId = getImportProjectId();
    if (!importProjectId) {
      message.warning(t('sqlPair.importSelectProjectFirst'));
      return;
    }
    setImporting(true);
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const resp = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/import`,
        {
          method: 'POST',
          headers: {
            'X-Project-Id': importProjectId,
          },
          body: JSON.stringify({
            useDefault: true,
            project_id: Number(importProjectId),
          }),
        }
      );
      const result = await resp.json();
      if (!resp.ok || !result.success) {
        throw new Error(result.message || String(resp.status));
      }
      const stats = result.data
        ? ` (${t('sqlPair.importStats', {
            created: result.data.created || 0,
            updated: result.data.updated || 0,
            failed: result.data.failed || 0,
          })})`
        : '';
      message.success(t('sqlPair.importDefaultCompleted') + stats);
      setImportVisible(false);
      setImportFile(null);
      setImportInputKey(k => k + 1);
      await loadData();
    } catch (e: any) {
      message.error(e?.message || t('sqlPair.importDefaultFailed'));
    } finally {
      setImporting(false);
    }
  };

  // ─────────────────────── 批量同步 ───────────────────────
  const handleBatchSync = async () => {
    if (selectedSqlPairs.length === 0) {
      message.warning(t('sqlPair.selectSqlPairsToSync'));
      return;
    }
    if (batchSyncing) return;
    setBatchSyncing(true);

    const unsynced = selectedSqlPairs.filter(sp => sp.is_synchronized === 0);
    if (unsynced.length === 0) {
      message.warning(t('sqlPair.allSelectedAlreadySynced'));
      setBatchSyncing(false);
      return;
    }

    try {
      message.loading(t('sqlPair.batchSyncing', { count: unsynced.length }), 0);
      for (const sp of unsynced) {
        await handleSync(sp);
      }
      message.destroy();
      message.success(t('sqlPair.batchSyncSuccess', { count: unsynced.length }));
      setSelectedRowKeys([]);
      setSelectedSqlPairs([]);
    } catch {
      message.destroy();
      message.error(t('sqlPair.batchSyncFailed'));
    } finally {
      setBatchSyncing(false);
    }
  };

  // ─────────────────────── 批量软删除 ───────────────────────
  const handleBatchDelete = async () => {
    if (selectedSqlPairs.length === 0) {
      message.warning(t('sqlPair.selectSqlPairsToDelete'));
      return;
    }
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const ids = selectedSqlPairs.map(sp => sp.id);
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/batch-delete`,
        {
          method: 'POST',
          body: JSON.stringify({ ids }),
        }
      );
      const result = await response.json();
      if (result.success) {
        message.success(t('sqlPair.batchDeleteCompleted'));
      } else {
        message.error(t('sqlPair.batchDeleteFailed'));
      }
      setSelectedRowKeys([]);
      setSelectedSqlPairs([]);
      loadData();
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error(t('sqlPair.batchDeleteFailed'));
    }
  };

  // ─────────────────────── 批量强制删除 ───────────────────────
  const handleBatchForceDelete = async () => {
    if (selectedSqlPairs.length === 0) {
      message.warning(t('sqlPair.selectSqlPairsToForceDelete'));
      return;
    }
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const ids = selectedSqlPairs.map(sp => sp.id);
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/batch-force-delete`,
        {
          method: 'POST',
          body: JSON.stringify({ ids }),
        }
      );
      const result = await response.json();
      if (result.success) {
        message.success(t('sqlPair.batchForceDeleteCompleted'));
      } else {
        message.error(t('sqlPair.batchForceDeleteFailed'));
      }
      setSelectedRowKeys([]);
      setSelectedSqlPairs([]);
      loadData();
    } catch (error) {
      console.error('批量强制删除失败:', error);
      message.error(t('sqlPair.batchForceDeleteFailed'));
    }
  };

  // ─────────────────────── 批量导出 ───────────────────────
  const handleBatchExport = () => {
    if (selectedSqlPairs.length === 0) {
      message.warning(t('sqlPair.selectSqlPairsToExport'));
      return;
    }
    const data = { data: { sqlPairs: selectedSqlPairs } };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sql-pairs-selected-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    message.success(t('sqlPair.batchExportSuccess', { count: selectedSqlPairs.length }));
  };

  // ─────────────────────── 强制删除（单行）───────────────────────
  const handleForceDelete = async (id: number) => {
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/sql-pairs/${id}/force`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(`Failed to force delete: ${response.status}`);
      }
      message.success(t('sqlPair.forceDeletedSuccess'));
      loadData();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to force delete sql-pair:', error);
      message.error(t('sqlPair.forceDeleteFailedWithReason', { reason: errorMessage }));
    }
  };

  // ─────────────────────── 过滤数据 ───────────────────────
  const filteredSqlPairs = sqlPairs.filter(sp => {
    const matchesSearch =
      !searchTerm ||
      sp.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sp.sql.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sp.optimized_query || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesIsSynchronized =
      filterIsSynchronized === '' ||
      (filterIsSynchronized === 'true'
        ? sp.is_synchronized === 1
        : sp.is_synchronized === 0);

    const projectIdValue =
      sp.project_id !== undefined && sp.project_id !== null ? String(sp.project_id) : '1';
    const matchesProjectId =
      selectedProjectId === 'all' ? true : projectIdValue === selectedProjectId;

    return matchesSearch && matchesIsSynchronized && matchesProjectId;
  });

  const activeSqlPairs = filteredSqlPairs.filter(sp => sp.is_active !== 0);
  const syncedSqlPairs = filteredSqlPairs.filter(sp => sp.is_synchronized === 1);
  const pendingSqlPairs = filteredSqlPairs.filter(sp => sp.is_synchronized !== 1);
  const statsScope = selectedProjectId === 'all' ? sqlPairs : filteredSqlPairs;
  const currentTenantCode = authService.getCurrentTenant() || 'default';

  // ─────────────────────── 表格列定义 ───────────────────────
  const columns = [
    {
      title: (
        <Checkbox
          checked={
            selectedRowKeys.length === filteredSqlPairs.length &&
            filteredSqlPairs.length > 0
          }
          indeterminate={
            selectedRowKeys.length > 0 &&
            selectedRowKeys.length < filteredSqlPairs.length
          }
          onChange={(e: CheckboxChangeEvent) => {
            if (e.target.checked) {
              setSelectedRowKeys(filteredSqlPairs.map(sp => sp.id));
              setSelectedSqlPairs(filteredSqlPairs);
            } else {
              setSelectedRowKeys([]);
              setSelectedSqlPairs([]);
            }
          }}
        />
      ),
      dataIndex: 'id',
      key: 'select',
      width: 60,
      render: (id: number, record: SqlPair) => (
        <Checkbox
          checked={selectedRowKeys.includes(id)}
          onChange={(e: CheckboxChangeEvent) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, id]);
              setSelectedSqlPairs([...selectedSqlPairs, record]);
            } else {
              setSelectedRowKeys(selectedRowKeys.filter(key => key !== id));
              setSelectedSqlPairs(selectedSqlPairs.filter(sp => sp.id !== id));
            }
          }}
        />
      ),
    },
    {
      title: t('sqlPair.colId'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a: SqlPair, b: SqlPair) => a.id - b.id,
      defaultSortOrder: 'ascend' as const,
      render: (id: number) => <Tag color="blue">{id}</Tag>,
    },
    {
      title: t('sqlPair.colQuestion'),
      dataIndex: 'question',
      key: 'question',
      width: 280,
      sorter: (a: SqlPair, b: SqlPair) => a.question.localeCompare(b.question),
      render: (question: string) => (
        <Tooltip
          title={question}
          overlayStyle={{ maxWidth: '500px' }}
          overlayInnerStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          <div
            style={{
              maxWidth: '260px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {question}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('sqlPair.colSql'),
      dataIndex: 'sql',
      key: 'sql',
      width: 280,
      render: (sql: string) => (
        <Tooltip
          overlayStyle={{
            maxWidth: 2400,
            maxHeight: '70vh',
            overflow: 'auto',
          }}
          overlayInnerStyle={{
            maxWidth: 2400,
            maxHeight: '70vh',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
          title={
            <pre
              style={{
                maxWidth: 2400,
                maxHeight: '70vh',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflow: 'auto',
                margin: 0,
              }}
            >
              {sql}
            </pre>
          }
        >
          <Text
            code
            style={{
              maxWidth: 260,
              cursor: 'pointer',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sql}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: t('sqlPair.colOptimizedQuery'),
      dataIndex: 'optimized_query',
      key: 'optimized_query',
      width: 180,
      render: (text: string | null) =>
        text ? (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 170,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {text}
            </div>
          </Tooltip>
        ) : (
          <Text type="secondary">{t('sqlPair.notSet')}</Text>
        ),
    },
    {
      title: t('sqlPair.colSyncStatus'),
      dataIndex: 'is_synchronized',
      key: 'isSynchronized',
      width: 120,
      sorter: (a: SqlPair, b: SqlPair) => a.is_synchronized - b.is_synchronized,
      render: (isSynchronized: number, record: SqlPair) => {
        if (record.id === syncErrorSqlPairId) {
          return <Tag color="red">{t('sqlPair.syncFailed')}</Tag>;
        }
        if (record.id === syncingSqlPairId) {
          return <Tag color="processing">{t('sqlPair.syncing')}</Tag>;
        }
        return (
          <Tag color={isSynchronized === 1 ? 'green' : 'orange'}>
            {isSynchronized === 1 ? t('sqlPair.synced') : t('sqlPair.unsynced')}
          </Tag>
        );
      },
    },
    {
      title: t('sqlPair.colExpiresAt'),
      dataIndex: 'expires_at',
      key: 'expiresAt',
      width: 150,
      sorter: (a: SqlPair, b: SqlPair) => (a.expires_at ?? 0) - (b.expires_at ?? 0),
      render: (expiresAt: number | null) =>
        expiresAt ? (
          <Text type="warning" style={{ fontSize: '12px' }}>
            {dayjs.unix(expiresAt).format('YYYY-MM-DD HH:mm')}
          </Text>
        ) : (
          <Tag color="green">{t('sqlPair.neverExpires')}</Tag>
        ),
    },
    {
      title: t('sqlPair.colCreatedAt'),
      dataIndex: 'created_at',
      key: 'createdAt',
      width: 150,
      sorter: (a: SqlPair, b: SqlPair) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return aTime - bTime;
      },
      render: (createdAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {createdAt ? new Date(createdAt).toLocaleString() : '-'}
        </Text>
      ),
    },
    {
      title: t('sqlPair.colUpdatedAt'),
      dataIndex: 'updated_at',
      key: 'updatedAt',
      width: 150,
      sorter: (a: SqlPair, b: SqlPair) => {
        const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return aTime - bTime;
      },
      render: (updatedAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {updatedAt ? new Date(updatedAt).toLocaleString() : t('sqlPair.unknown')}
        </Text>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: unknown, record: SqlPair) => {
        const deleteMenuItems: MenuProps['items'] = [
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: t('common.delete'),
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: t('sqlPair.confirmDelete'),
                content: t('sqlPair.cannotUndo'),
                okText: t('sqlPair.confirmDelete'),
                cancelText: t('common.cancel'),
                okButtonProps: { danger: true },
                onOk: () => handleDelete(record.id),
              });
            },
          },
          {
            key: 'forceDelete',
            icon: <DeleteOutlined />,
            label: t('sqlPair.batchForceDelete').replace('批量', ''),
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: t('sqlPair.confirmForceDelete'),
                content: t('sqlPair.forceDeleteWarning'),
                okText: t('sqlPair.confirmForceDelete'),
                cancelText: t('common.cancel'),
                okButtonProps: { danger: true },
                onOk: () => handleForceDelete(record.id),
              });
            },
          },
        ];

        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              {t('common.edit')}
            </Button>
            <Button
              type="link"
              size="small"
              icon={<SyncOutlined spin={record.id === syncingSqlPairId} />}
              onClick={() => handleSync(record)}
              loading={record.id === syncingSqlPairId}
              danger={record.id === syncErrorSqlPairId}
            >
              {record.id === syncingSqlPairId
                ? t('sqlPair.syncing')
                : record.id === syncErrorSqlPairId
                ? t('sqlPair.retry')
                : t('sqlPair.sync')}
            </Button>
            <Dropdown menu={{ items: deleteMenuItems }} trigger={['click']}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  // ─────────────────────── 生命周期 ───────────────────────
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const handleTenantChange = () => {
      setSelectedProjectId('all');
      loadData();
    };
    const handleSyncCompleted = (event: Event) => {
      const detail = (event as CustomEvent<{
        targetTenant?: string;
        targetProject?: string;
        entityTypes?: string[];
      }>).detail;
      if (detail?.entityTypes?.includes('sql_pair') && detail.targetProject) {
        setSelectedProjectId(detail.targetProject);
      }
      loadData();
    };
    window.addEventListener('storage', handleTenantChange);
    window.addEventListener('tenantChanged', handleTenantChange);
    window.addEventListener('syncCompleted', handleSyncCompleted);
    return () => {
      window.removeEventListener('storage', handleTenantChange);
      window.removeEventListener('tenantChanged', handleTenantChange);
      window.removeEventListener('syncCompleted', handleSyncCompleted);
    };
  }, []);

  useEffect(() => {
    if (selectedProjectId === 'all' || loading) return;
    const ok = projectSelectOptions.some((o) => o.value === selectedProjectId);
    if (!ok) setSelectedProjectId('all');
  }, [selectedProjectId, projectSelectOptions, loading]);

  useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedSqlPairs([]);
  }, [selectedProjectId]);

  // ─────────────────────── 渲染 ───────────────────────
  return (
    <div style={{ padding: '24px' }}>
      {/* 同步错误条幅 */}
      {syncErrorMessage && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: 6,
            position: 'relative',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text type="danger" style={{ marginRight: 8 }}>
                <SyncOutlined style={{ marginRight: 4 }} />
                {t('sqlPair.syncFailed')}
              </Text>
              <Text type="secondary">{syncErrorMessage}</Text>
            </div>
            <Button
              type="text"
              size="small"
              onClick={() => setSyncErrorMessage('')}
              style={{ color: '#999' }}
            >
              ✕
            </Button>
          </div>
        </div>
      )}

      {/* 标题区域 */}
      <div style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="start" style={{ flexWrap: 'wrap' }}>
          <Space align="baseline" size={16} style={{ flexWrap: 'wrap' }}>
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: '8px' }} />
              {t('sqlPair.title')}
            </Title>
            <Select
              style={{ width: 240 }}
              value={selectedProjectId}
              onChange={(value) => setSelectedProjectId(value)}
              options={projectSelectOptions}
            />
            <Text type="secondary">{t('sqlPair.subtitle')}</Text>
            <Space style={{ flexWrap: 'wrap' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                {t('sqlPair.create')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={backupLoading}
                onClick={handleBackupSqlPairs}
              >
                {t('sqlPair.backup')}
              </Button>
              <Button
                icon={<CloudDownloadOutlined />}
                loading={backupLoading}
                onClick={handleBackupRemoteSqlPairs}
              >
                {t('sqlPair.backupRemote')}
              </Button>
              {isSuperAdmin && (
                <Button icon={<FileTextOutlined />} onClick={handleShowBackupFiles}>
                  {t('sqlPair.backupFiles')}
                </Button>
              )}
              <Button icon={<UploadOutlined />} onClick={() => setImportVisible(true)}>
                {t('sqlPair.import')}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                {t('sqlPair.refreshData')}
              </Button>
              <Button icon={<ExportOutlined />} onClick={exportData}>
                {t('sqlPair.exportData')}
              </Button>
            </Space>
          </Space>
        </Row>
      </div>

      {/* 统计栏 */}
      <Row gutter={16} align="stretch" style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card
            style={{
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Space size={48} align="center" wrap>
              <Statistic
                title={t('sqlPair.stat.total')}
                value={statsScope.length}
                prefix={<FileTextOutlined />}
              />
              <Statistic
                title={t('sqlPair.stat.active')}
                value={activeSqlPairs.length}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
              <Statistic
                title={t('sqlPair.stat.synced')}
                value={syncedSqlPairs.length}
                valueStyle={{ color: '#1890ff' }}
                prefix={<SyncOutlined />}
              />
              <Statistic
                title={t('sqlPair.stat.pending')}
                value={pendingSqlPairs.length}
                valueStyle={{ color: '#faad14' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Space>
          </Card>
        </Col>
      </Row>

      {!loading && sqlPairs.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('sqlPair.emptyTenantHint', { tenant: currentTenantCode })}
        />
      )}

      {!loading && sqlPairs.length > 0 && filteredSqlPairs.length === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('sqlPair.emptyProjectHint')}
        />
      )}

      <Card>
        {/* 过滤栏 */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            allowClear
            style={{ width: 260 }}
            placeholder={t('sqlPair.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Tooltip title={t('sqlPair.filter.syncStatusTip')} placement="top">
            <Select
              placeholder={t('sqlPair.filter.syncStatusPlaceholder')}
              value={filterIsSynchronized || undefined}
              onChange={(v) => setFilterIsSynchronized(v ?? '')}
              allowClear
              style={{ width: 180 }}
            >
              <Option value="true">{t('sqlPair.synced')}</Option>
              <Option value="false">{t('sqlPair.unsynced')}</Option>
            </Select>
          </Tooltip>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSearchTerm('');
              setFilterIsSynchronized('');
            }}
          >
            {t('sqlPair.resetFilters')}
          </Button>
        </Space>

        {/* 多选操作栏 */}
        {selectedRowKeys.length > 0 && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <Text type="secondary">
                {t('sqlPair.selectedCount', { count: selectedRowKeys.length })}
              </Text>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={handleBatchSync}
                  loading={loading || batchSyncing}
                >
                  {t('sqlPair.batchSync')}
                </Button>
                <Popconfirm
                  title={t('sqlPair.confirmBatchDelete')}
                  description={t('sqlPair.cannotUndo')}
                  onConfirm={handleBatchDelete}
                  okText={t('sqlPair.confirmDelete')}
                  cancelText={t('common.cancel')}
                  okButtonProps={{ danger: true }}
                  placement="topRight"
                >
                  <Button danger icon={<DeleteOutlined />} loading={loading}>
                    {t('sqlPair.batchDelete')}
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title={t('sqlPair.confirmBatchForceDelete')}
                  description={t('sqlPair.forceDeleteWarning')}
                  onConfirm={handleBatchForceDelete}
                  okText={t('sqlPair.confirmForceDelete')}
                  cancelText={t('common.cancel')}
                  okButtonProps={{ danger: true }}
                  placement="topRight"
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={loading}
                    style={{ borderColor: '#cf1322', color: '#cf1322' }}
                  >
                    {t('sqlPair.batchForceDelete')}
                  </Button>
                </Popconfirm>
                <Button icon={<ExportOutlined />} onClick={handleBatchExport}>
                  {t('sqlPair.batchExport')}
                </Button>
              </Space>
            </div>
          </div>
        )}

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={filteredSqlPairs}
          loading={loading}
          rowKey="id"
          rowClassName={(record: SqlPair) => (record.is_active === 0 ? 'row-deleted' : '')}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              t('sqlPair.paginationTotal', { total, from: range[0], to: range[1] }),
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            onShowSizeChange: (current, size) => {
              setCurrentPage(1);
              setPageSize(size);
            },
          }}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>

      {/* 编辑/创建模态框 */}
      <Modal
        title={
          isEditMode ? t('sqlPair.modal.editTitle') : t('sqlPair.modal.createTitle')
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={840}
        centered
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={editingSqlPair || undefined}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="ID" name="id">
                <Input disabled placeholder={t('sqlPair.autoGenerated')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('sqlPair.syncStatus')} name="is_synchronized">
                <Select placeholder={t('sqlPair.selectSyncStatus')}>
                  <Option value={1}>{t('sqlPair.synced')}</Option>
                  <Option value={0}>{t('sqlPair.unsynced')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t('sqlPair.form.question')}
            name="question"
            rules={[{ required: true, message: t('sqlPair.form.questionRequired') }]}
          >
            <TextArea rows={3} showCount maxLength={1000} />
          </Form.Item>

          <Form.Item
            label={t('sqlPair.form.sql')}
            name="sql"
            rules={[{ required: true, message: t('sqlPair.form.sqlRequired') }]}
          >
            <TextArea
              autoSize={{ minRows: 4, maxRows: 12 }}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>

          <Form.Item label={t('sqlPair.form.optimizedQuery')} name="optimized_query">
            <Input />
          </Form.Item>

          <Form.Item label={t('sqlPair.form.businessRules')} name="business_rules">
            <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>

          <Form.Item label={t('sqlPair.form.expiresAt')} name="expires_at">
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>

          <Divider />

          <div style={{ textAlign: 'right' }}>
            <Space>
              {isEditMode && editingSqlPair && (
                <Button
                  icon={<SyncOutlined />}
                  onClick={() => {
                    setModalVisible(false);
                    const original = sqlPairs.find(sp => sp.id === editingSqlPair.id);
                    if (original) {
                      handleSync(original);
                    }
                  }}
                >
                  {t('sqlPair.syncToRemote')}
                </Button>
              )}
              <Button onClick={() => setModalVisible(false)}>{t('common.cancel')}</Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                {isEditMode ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* 导入模态框 */}
      <Modal
        title={t('sqlPair.import')}
        open={importVisible}
        onCancel={() => {
          setImportVisible(false);
          setImportFile(null);
          setImportInputKey(k => k + 1);
        }}
        footer={null}
        width={600}
        centered
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            {t('sqlPair.importTargetProject', {
              project:
                selectedProjectId !== 'all'
                  ? projectSelectOptions.find((o) => o.value === selectedProjectId)?.label ||
                    selectedProjectId
                  : getImportProjectId()
                  ? projectSelectOptions.find((o) => o.value === getImportProjectId())?.label ||
                    getImportProjectId()
                  : t('sqlPair.importSelectProjectFirst'),
            })}
          </Text>
          <input
            type="file"
            accept="application/json,.json"
            key={importInputKey}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
              setImportFile(f);
            }}
          />
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setImportVisible(false);
                  setImportFile(null);
                  setImportInputKey(k => k + 1);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleImportDefault} loading={importing}>
                {t('sqlPair.importDefault')}
              </Button>
              <Button type="primary" loading={importing} onClick={handleImportFromJson}>
                {t('sqlPair.import')}
              </Button>
            </Space>
          </div>
        </Space>
      </Modal>

      {/* 备份文件列表模态框（仅超级管理员）*/}
      {isSuperAdmin && (
        <Modal
          title={t('sqlPair.backupFileList')}
          open={backupFilesVisible}
          onCancel={() => setBackupFilesVisible(false)}
          footer={[
            <Button key="close" onClick={() => setBackupFilesVisible(false)}>
              {t('common.close')}
            </Button>,
          ]}
          width={800}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{t('sqlPair.backupFileListDesc')}</Text>
          </div>

          {backupFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div>{t('sqlPair.noBackupFiles')}</div>
            </div>
          ) : (
            <Table
              dataSource={backupFiles}
              rowKey="filename"
              pagination={false}
              size="small"
              columns={[
                {
                  title: t('sqlPair.fileName'),
                  dataIndex: 'filename',
                  key: 'filename',
                  render: (filename: string) => (
                    <Space>
                      <FileTextOutlined />
                      <Text code>{filename}</Text>
                    </Space>
                  ),
                },
                {
                  title: t('sqlPair.fileSize'),
                  dataIndex: 'size',
                  key: 'size',
                  render: (size: number) => (
                    <Text type="secondary">{(size / 1024).toFixed(1)} KB</Text>
                  ),
                },
                {
                  title: t('sqlPair.createdAt'),
                  dataIndex: 'created',
                  key: 'created',
                  render: (created: string) => (
                    <Text type="secondary">{new Date(created).toLocaleString()}</Text>
                  ),
                },
                {
                  title: t('common.actions'),
                  key: 'actions',
                  render: (_: unknown, record: any) => (
                    <Space>
                      <Tooltip title="JSON">
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadBackupFile(record.filename)}
                        >
                          JSON
                        </Button>
                      </Tooltip>
                      <Tooltip title="Excel">
                        <Button
                          type="link"
                          icon={<FileExcelOutlined />}
                          onClick={() => handleDownloadBackupFileAsExcel(record.filename)}
                        >
                          Excel
                        </Button>
                      </Tooltip>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default SqlPairManager;
